import { Chart as ChartJS, ArcElement, Tooltip, Legend, LegendItem } from 'chart.js';
import React, { FC, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { colors, fontFamilyPrimary } from '../../../theme';
import { CALCULATION_MODE } from '../../Modals/DashboardModal/constants';
import { Evolution } from '../dashboardInterfaces';
import { drawEvolutionIcon, evolutionIconPlugin, getEvolutionIconGap, getEvolutionIconSize, withEvolutionMarker } from './evolution';

interface DonutCardProps {
    donutIndication: {
        keyFigure: string | {key:string, value: number},
        labels: string[]
        hasIndicatorKpiGroup: boolean,
        datasets: Array<{
            label: string
            data: (string | number)[]
            backgroundColor?: string[]
            hoverOffset?: number
        }>,
        round: number
        base: number
        comparedKeyFigure?: { [label: string]: number }
        comparedValues?: { [label: string]: number }
        comparisonType?: string
        keyFigureEvolution?: Evolution | null
        valueEvolutions?: { [label: string]: Evolution | null }
    }
}

ChartJS.register(ArcElement, Tooltip, Legend);

// The compared figure sits under the key figure, at a fraction of its size.
const COMPARISON_FONT_RATIO = 0.32;
const EM_IN_PIXELS = 16;
// The key figure is drawn in large digits, a full height icon would outweigh them.
const KEY_FIGURE_ICON_RATIO = 0.6;

const LEGEND_MAX_WIDTH = 200;
const LEGEND_BOX_WIDTH = 32;
const LEGEND_BOX_HEIGHT = 12;
const LEGEND_PADDING = 20;
const LEGEND_FONT_SIZE = 14;
const LEGEND_ITEM_HEIGHT = Math.max(LEGEND_FONT_SIZE, LEGEND_BOX_HEIGHT);
const ELLIPSIS = '…';

// With the legend on its right, the chart is twice as wide as high, up to a cap.
const ASPECT_RATIO = 2;
const MAX_HEIGHT_WITH_LEGEND_ON_THE_RIGHT = 350;
// With the legend below, the donut keeps this height and the canvas grows with the legend.
const MIN_DONUT_HEIGHT = 220;

// Mirrors the column layout of the Chart.js legend: an item opens a new column
// once the column, the item and two paddings no longer fit in the height.
const legendFitsInOneColumn = (itemCount: number, height: number) => (
    itemCount <= 1
    || ((itemCount - 1) * (LEGEND_ITEM_HEIGHT + LEGEND_PADDING)) + LEGEND_ITEM_HEIGHT + (2 * LEGEND_PADDING) <= height
);

// Chart.js reserves maxWidth for the legend but draws each label at full
// length, so a long one runs past the canvas and gets cut by the card edge.
// The label is shortened to what fits, the tooltip still shows it in full.
const generateTruncatedLabels = (chart: ChartJS) => {
    const items = ChartJS.overrides.doughnut.plugins.legend.labels.generateLabels(chart);
    // On a narrow card the layout gives the legend less than LEGEND_MAX_WIDTH.
    // Chart.js sets that width on the legend before building its labels, but
    // does not type it.
    const layoutWidth = (chart.legend as { maxWidth?: number } | undefined)?.maxWidth;
    const legendWidth = Math.min(LEGEND_MAX_WIDTH, layoutWidth ?? LEGEND_MAX_WIDTH);
    // Mirrors the item width of the Chart.js legend: padding, box, then half a
    // font size of gap before the text.
    const maxTextWidth = legendWidth - LEGEND_PADDING - LEGEND_BOX_WIDTH - (LEGEND_FONT_SIZE / 2);
    const { ctx } = chart;
    ctx.save();
    ctx.font = `${LEGEND_FONT_SIZE}px ${fontFamilyPrimary}`;
    const truncatedItems = items.map((item) => {
        if (ctx.measureText(item.text).width <= maxTextWidth) {
            return item;
        }
        let { text } = item;
        while (text.length > 0 && ctx.measureText(`${text}${ELLIPSIS}`).width > maxTextWidth) {
            text = text.slice(0, -1);
        }

        return { ...item, text: `${text.trimEnd()}${ELLIPSIS}` };
    });
    ctx.restore();

    return truncatedItems;
};

const DonutCard: FC<DonutCardProps> = ({ donutIndication }) => {
    const { t } = useTranslation('dashboard');
    const [chartWidth, setChartWidth] = useState<number | null>(null);
    const [bottomLegendHeight, setBottomLegendHeight] = useState(0);
    // Decided on the height the chart has with the legend on its right, which
    // only follows the width: growing the canvas for a legend below cannot
    // bring the legend back to the right.
    const isLegendOnTheRight = chartWidth === null || legendFitsInOneColumn(
        donutIndication.labels.length,
        Math.min(chartWidth / ASPECT_RATIO, MAX_HEIGHT_WITH_LEGEND_ON_THE_RIGHT),
    );
    const comparisonLabel = donutIndication.comparisonType === 'TEMPORAL' ? t('compareToGlobal.temporalLegend') : t('compareToGlobal.legend');
    // Memoize the plugin to ensure it updates when donutIndication changes
    // react-chartjs-2 only reads the plugins prop when the chart is built, so the
    // plugin below keeps drawing with the label and figure it captured. Anything
    // it paints has to remount the chart, not just update it.
    const centerTextKey = JSON.stringify([
        donutIndication.keyFigure,
        donutIndication.comparedKeyFigure,
        donutIndication.comparisonType,
        donutIndication.keyFigureEvolution,
    ]);
    const plugins = useMemo(() => [evolutionIconPlugin, {
        id: 'donutLegendLayout',
        afterLayout(chart: ChartJS) {
            setChartWidth(chart.width);
            if (chart.options.plugins?.legend?.position === 'bottom' && chart.legend) {
                setBottomLegendHeight(chart.legend.height);
            }
        },
    }, {
        id: `donut-${centerTextKey}-${Date.now()}`, // Unique ID that changes with data
        beforeDraw(chart: any) {
            // Calculate text inside the plugin to ensure it's always current
            const computeMethod = Object.keys(donutIndication.keyFigure)[0];
            const isNPSPositive = computeMethod === CALCULATION_MODE.NPS && donutIndication.keyFigure && Object.values(donutIndication.keyFigure)[0] > 0;
            const isAverage = computeMethod === CALCULATION_MODE.AVERAGE;
            const isNPS = computeMethod === CALCULATION_MODE.NPS;
            const plus = isNPSPositive ? '+' : '';
            const percent = !isNPSPositive && !isAverage && !isNPS ? '%' : '';
            const textInsideDonut = Object.values(donutIndication.keyFigure).length !== 0 ? `${plus}${Number(Object.values(donutIndication.keyFigure)).toFixed(donutIndication.round)}${percent}` : '';
            const key = Object.keys(donutIndication.keyFigure)[0];
            // Looked up by index, the legend text may be truncated.
            const keyIndex = donutIndication.labels.indexOf(key);
            const textColor = chart.legend.legendItems?.find(({ index }: LegendItem) => index === keyIndex)?.fillStyle || colors.grey200;
            const { ctx } = chart;
            // The hole of the donut, wherever the legend sits.
            const { left, top, width, height } = chart.chartArea;
            const { legend } = chart;
            ctx.restore();
            const fontSize = (height / 80).toFixed(2);
            ctx.font = `700 ${fontSize}em  ${fontFamilyPrimary}`;
            ctx.textBaseline = 'top';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = textColor;
            const text = textInsideDonut;
            const textX = Math.round(left + ((width - ctx.measureText(text).width) / 2));
            const textY = top + (height / 2);
            legend.legendHitBoxes = undefined;
            ctx.fillText(text, textX, textY);
            const keyFigurePixels = parseFloat(fontSize) * EM_IN_PIXELS;
            if (donutIndication.keyFigureEvolution && text) {
                // The key figure stays centred, its evolution sits right against
                // its last glyph so it stays inside the hole of the donut.
                const keyFigureIconSize = getEvolutionIconSize(keyFigurePixels) * KEY_FIGURE_ICON_RATIO;
                drawEvolutionIcon(
                    ctx,
                    donutIndication.keyFigureEvolution,
                    textX + ctx.measureText(text).actualBoundingBoxRight + getEvolutionIconGap(keyFigureIconSize),
                    textY,
                    keyFigureIconSize,
                    // A share takes the color of its modality, a score has none of its own.
                    isAverage || isNPS ? colors.grey300 : textColor,
                );
            }

            const comparedFigure = donutIndication.comparedKeyFigure && Object.values(donutIndication.comparedKeyFigure)[0];
            if (comparedFigure != null && donutIndication.base > 0) {
                const comparedPlus = isNPS && comparedFigure > 0 ? '+' : '';
                const comparedValueText = `${comparedPlus}${Number(comparedFigure).toFixed(donutIndication.round)}${percent}`;
                // Canvas fillText ignores line breaks, so the temporal label gets its own line.
                const comparedLines = donutIndication.comparisonType === 'TEMPORAL'
                    ? [`${comparisonLabel} :`, comparedValueText]
                    : [`${comparisonLabel} : ${comparedValueText}`];
                const comparisonPixels = keyFigurePixels * COMPARISON_FONT_RATIO;
                ctx.font = `400 ${(parseFloat(fontSize) * COMPARISON_FONT_RATIO).toFixed(2)}em ${fontFamilyPrimary}`;
                ctx.fillStyle = colors.grey400;
                comparedLines.forEach((line, index) => {
                    ctx.fillText(
                        line,
                        Math.round(left + ((width - ctx.measureText(line).width) / 2)),
                        textY + (keyFigurePixels / 2) + (comparisonPixels * (index + 1)),
                    );
                });
            }
            ctx.save();
        },
    }], [donutIndication, comparisonLabel, centerTextKey]); // Dependency array ensures plugin recreates when data changes

    return (
        // With the legend below, the chart fills this box instead of following
        // its aspect ratio, so the legend never eats into the donut.
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: isLegendOnTheRight ? undefined : MIN_DONUT_HEIGHT + bottomLegendHeight,
            }}
        >
            <Doughnut
                // Remount when anything the plugin draws changes, and when the legend
                // moves: Chart.js does not resize back to its aspect ratio by itself.
                key={`${centerTextKey}-${isLegendOnTheRight}`}
                data={donutIndication}
                height={100}
                options={{
                    plugins: {
                        legend: {
                            // Below the donut once the labels need a second column on its right.
                            position: isLegendOnTheRight ? 'right' : 'bottom',
                            fullSize: true,
                            labels: {
                                boxWidth: LEGEND_BOX_WIDTH,
                                boxHeight: LEGEND_BOX_HEIGHT,
                                padding: LEGEND_PADDING,
                                color: colors.grey200,
                                font: {
                                    family: fontFamilyPrimary,
                                    size: LEGEND_FONT_SIZE,
                                },
                                generateLabels: generateTruncatedLabels,
                            },
                            maxHeight: 1200,
                            // Below the donut the legend spreads over the whole width.
                            maxWidth: isLegendOnTheRight ? LEGEND_MAX_WIDTH : undefined,
                        },
                        datalabels: {
                            display: false,
                        },
                        tooltip: {
                            borderWidth: 1,
                            titleColor: '#ffffff',
                            bodyColor: '#ffffff',
                            bodyFont: { family: fontFamilyPrimary },
                            displayColors: false,
                            footerColor: colors.grey500,
                            footerFont: { family: fontFamilyPrimary, weight: 'normal' as const },
                            footerMarginTop: 4,
                            callbacks: {
                                label: (dataset: any) => withEvolutionMarker(
                                    `${dataset.label}: ${dataset.raw.toFixed(donutIndication.round)}%`,
                                    donutIndication.valueEvolutions?.[dataset.label] ?? null,
                                ),
                                footer: (datasets: any) => {
                                    const comparedValue = donutIndication.comparedValues?.[datasets[0]?.label];

                                    return (comparedValue == null || donutIndication.base <= 0)
                                        ? []
                                        : [`${comparisonLabel} : ${Number(comparedValue).toFixed(donutIndication.round)}%`];
                                },
                            },
                        },
                    },
                    layout: {
                        padding: 0,
                    },
                    cutout: '80%',
                    responsive: true,
                    maintainAspectRatio: isLegendOnTheRight,
                    aspectRatio: ASPECT_RATIO,
                    onHover: (e) => {
                        const doughnutElement = e.native?.target as HTMLInputElement;
                        doughnutElement.style.cursor = 'pointer';
                    },
                }}
                plugins={plugins}
                style={isLegendOnTheRight ? { maxHeight: `${MAX_HEIGHT_WITH_LEGEND_ON_THE_RIGHT}px` } : undefined}
                width={100}
            />
        </div>
    );
};

export default DonutCard;
